// Progression (§7.3, §7.4): XP and levels, Bonk Bucks, match rewards, unlock gating and the daily
// shop. Pure functions over Profile values (no DOM, no storage, no bus); `ProfileService` applies
// them and persists. `profile.level` is the Bean Level (1–40) and `profile.xp` the XP earned
// inside that level (0 ≤ xp < xpForLevel(level)); `profile.totalXp` is lifetime XP.
import { TUNE } from '@shared/constants';
import { CATALOG, catalogIndex, catalogItem, HAT_NONE, IDENTITIES, NO_IDENTITY_PREF, type CatalogItem, type CosmeticCategory } from '@shared/cosmetics';
import { hashString, seedRng, shuffle, type RngHolder } from '@shared/rng';
import { ARENAS, type MapId } from '@shared/ids';
import type { MatchEndDTO } from '@shared/protocol/messages';
import type { Profile } from './Profile';

const P = TUNE.progression;
export const MAX_LEVEL = P.maxLevel;
/**
 * At the level cap XP keeps accruing (§7.4 "XP keeps accruing for Bucks"): every full level-40
 * bar past the cap pays this many Bonk Bucks.
 */
export const CAP_BONUS_BUCKS = 100;

/** XP to go from level n to n+1: 300 + 60·(n − 1) (§7.4). At the cap, the size of the bonus bar. */
export function xpForLevel(level: number): number {
  return P.levelBaseXp + P.levelStepXp * (Math.max(1, Math.min(MAX_LEVEL, level)) - 1);
}

/** Total XP needed to reach `level` from a fresh level-1 profile. */
export function totalXpForLevel(level: number): number {
  let sum = 0;
  for (let n = 1; n < Math.min(level, MAX_LEVEL); n++) sum += xpForLevel(n);
  return sum;
}

export interface LevelInfo {
  level: number;
  /** XP inside the current level. */
  xp: number;
  /** XP the current level needs (the bonus bar size at the cap). */
  need: number;
  /** Bar fill 0..1. */
  frac: number;
  capped: boolean;
}

export function levelInfo(p: Pick<Profile, 'level' | 'xp'>): LevelInfo {
  const need = xpForLevel(p.level);
  const xp = Math.max(0, Math.min(need, p.xp));
  return { level: p.level, xp, need, frac: need > 0 ? xp / need : 0, capped: p.level >= MAX_LEVEL };
}

export interface XpResult { level: number; xp: number; levelsGained: number[]; capBonusBucks: number }

/** Adds `gain` XP to (level, xp-in-level), levelling up as often as it fills (§7.4). */
export function addXp(level: number, xp: number, gain: number): XpResult {
  let lv = Math.max(1, Math.min(MAX_LEVEL, level));
  let x = Math.max(0, xp) + Math.max(0, Math.floor(gain));
  const levelsGained: number[] = [];
  let capBonusBucks = 0;
  while (lv < MAX_LEVEL && x >= xpForLevel(lv)) {
    x -= xpForLevel(lv);
    lv++;
    levelsGained.push(lv);
  }
  if (lv >= MAX_LEVEL) {
    const bar = xpForLevel(MAX_LEVEL);
    while (x >= bar) {
      x -= bar;
      capBonusBucks += CAP_BONUS_BUCKS;
    }
  }
  return { level: lv, xp: x, levelsGained, capBonusBucks };
}

/** Items unlocked by reaching levels in (from, to] (§7.3: one per level 2–40). */
export function unlocksBetween(from: number, to: number): CatalogItem[] {
  return CATALOG.filter((it) => it.unlockLevel >= 2 && it.unlockLevel > from && it.unlockLevel <= to).sort((a, b) => a.unlockLevel - b.unlockLevel);
}

// ---- Match rewards ----------------------------------------------------------------------------

/** What the local player did this match, gathered by ProfileService from the bus. */
export interface MatchLedger {
  localSlot: number;
  team: number;
  /** Local placings per scoring round (1 = won the round). */
  roundPlaces: number[];
  /** Challenges completed during this match (their XP/Bucks were granted on completion). */
  challengesCompleted: number[];
}

export interface RewardLine { id: 'participation' | 'kos' | 'roundWins' | 'roundTop3' | 'awards' | 'challenges' | 'matchWin'; label: string; count: number; xp: number }

export interface MatchRewards {
  lines: RewardLine[];
  /** XP from this match's lines (challenge XP included; it was granted when completed). */
  xp: number;
  /** Bonk Bucks from the match formula (capped at 80), plus challenge and cap bonuses. */
  bucks: number;
  bucksMatch: number;
  bucksChallenges: number;
  bucksCapBonus: number;
  kos: number;
  won: boolean;
  place: number;
  levelBefore: number; xpBefore: number;
  levelAfter: number; xpAfter: number;
  /** Cosmetics unlocked by the levels gained, in level order. */
  unlocks: CatalogItem[];
  challengesCompleted: number[];
}

/** XP lines for one match (§7.4 table). A round win pays 40; 2nd/3rd place pay the top-3 15. */
export function matchXpLines(end: MatchEndDTO, ledger: MatchLedger): { lines: RewardLine[]; kos: number; won: boolean; place: number } {
  const x = P.xp;
  const me = ledger.localSlot;
  const st = end.stats[me];
  const kos = st ? st.kos + st.ghostKos : 0;
  const standing = end.standings.find((s) => s.slot === me);
  const place = standing?.place ?? 0;
  const won = place === 1;
  const roundWins = ledger.roundPlaces.filter((p) => p === 1).length;
  const top3 = ledger.roundPlaces.filter((p) => p === 2 || p === 3).length;
  const awards = end.awards.filter((a) => a.slot === me).length;
  const ch = ledger.challengesCompleted.length;
  const lines: RewardLine[] = [
    { id: 'participation', label: 'Played a match', count: 1, xp: x.participation },
    { id: 'kos', label: 'KOs', count: kos, xp: kos * x.perKo },
    { id: 'roundWins', label: 'Round wins', count: roundWins, xp: roundWins * x.roundWin },
    { id: 'roundTop3', label: 'Top-3 finishes', count: top3, xp: top3 * x.roundTop3 },
    { id: 'awards', label: 'Awards', count: awards, xp: awards * x.award },
    { id: 'challenges', label: 'Challenges', count: ch, xp: ch * x.challenge },
    { id: 'matchWin', label: 'Match winner', count: won ? 1 : 0, xp: won ? x.matchWin : 0 },
  ];
  return { lines, kos, won, place };
}

/** Bonk Bucks for a match: 40 + 5·KOs + 20·(won), capped at 80 (§7.4). */
export function matchBucks(kos: number, won: boolean): number {
  return Math.min(P.bucks.cap, P.bucks.base + P.bucks.perKo * Math.max(0, kos) + (won ? P.bucks.matchWin : 0));
}

// ---- Unlock gating (§7.3) --------------------------------------------------------------------

export type ItemState = 'starter' | 'level' | 'owned' | 'locked' | 'shop';

export interface ItemStatus {
  item: CatalogItem | null;
  unlocked: boolean;
  /** Why it is (un)locked: starter, reached its level, bought; locked by level; shop-only. */
  state: ItemState;
  unlockLevel: number;
  price: number;
  /** In today's shop rotation (shop-only items). */
  inShop: boolean;
  equipped: boolean;
}

export function isOwned(p: Pick<Profile, 'owned'>, cat: CosmeticCategory, id: number): boolean {
  return p.owned.includes(catalogIndex(cat, id));
}

/** True when the profile may equip `id` in `cat` (hat "none" always). */
export function isUnlocked(p: Pick<Profile, 'level' | 'owned'>, cat: CosmeticCategory, id: number): boolean {
  if (cat === 'hat' && id === HAT_NONE) return true;
  const it = catalogItem(cat, id);
  if (!it) return false;
  if (it.unlockLevel === 1) return true;
  if (it.unlockLevel >= 2 && p.level >= it.unlockLevel) return true;
  return isOwned(p, cat, id);
}

export function itemStatus(p: Profile, cat: CosmeticCategory, id: number, date: string): ItemStatus {
  const it = catalogItem(cat, id) ?? null;
  const equipped = p.cosmetics[cat] === id;
  if (cat === 'hat' && id === HAT_NONE) return { item: null, unlocked: true, state: 'starter', unlockLevel: 1, price: 0, inShop: false, equipped };
  if (!it) return { item: null, unlocked: false, state: 'locked', unlockLevel: 0, price: 0, inShop: false, equipped };
  const owned = isOwned(p, cat, id);
  const inShop = it.unlockLevel === 0 && shopForDate(date).some((s) => s.cat === cat && s.id === id);
  let state: ItemState;
  if (it.unlockLevel === 1) state = 'starter';
  else if (it.unlockLevel >= 2 && p.level >= it.unlockLevel) state = 'level';
  else if (owned) state = 'owned';
  else state = it.unlockLevel === 0 ? 'shop' : 'locked';
  const unlocked = state === 'starter' || state === 'level' || state === 'owned';
  return { item: it, unlocked, state, unlockLevel: it.unlockLevel, price: it.price, inShop, equipped };
}

// ---- Shop (§7.3: 6 of the 9 shop-only items, rotating daily by the UTC date) -------------------

export function shopForDate(date: string): CatalogItem[] {
  const pool = CATALOG.filter((it) => it.unlockLevel === 0);
  const h: RngHolder = { rng: seedRng(hashString(`shop:${date}`)) };
  return shuffle(h, [...pool]).slice(0, P.shop.slots).sort((a, b) => a.price - b.price || catalogIndex(a.cat, a.id) - catalogIndex(b.cat, b.id));
}

export type BuyResult = 'ok' | 'owned' | 'notInShop' | 'tooPoor' | 'notForSale';

/** Buys a shop item for today: spends Bucks and records ownership. */
export function buyItem(p: Profile, cat: CosmeticCategory, id: number, date: string): { result: BuyResult; profile: Profile } {
  const it = catalogItem(cat, id);
  if (!it || it.unlockLevel !== 0 || it.price <= 0) return { result: 'notForSale', profile: p };
  if (isOwned(p, cat, id)) return { result: 'owned', profile: p };
  if (!shopForDate(date).some((s) => s.cat === cat && s.id === id)) return { result: 'notInShop', profile: p };
  if (p.bucks < it.price) return { result: 'tooPoor', profile: p };
  return { result: 'ok', profile: { ...p, bucks: p.bucks - it.price, owned: [...p.owned, catalogIndex(cat, id)] } };
}

/** Equips an unlocked cosmetic (`identityPref` takes 0–7 or NO_IDENTITY_PREF); null when locked/invalid. */
export function equipItem(p: Profile, cat: CosmeticCategory | 'identityPref', id: number): Profile | null {
  if (cat === 'identityPref') {
    if (id !== NO_IDENTITY_PREF && !(Number.isInteger(id) && id >= 0 && id < IDENTITIES.length)) return null;
  } else if (!isUnlocked(p, cat, id)) {
    return null;
  }
  if (p.cosmetics[cat] === id) return p;
  return { ...p, cosmetics: { ...p.cosmetics, [cat]: id } };
}

/** Replaces any equipped cosmetic the profile may not use (e.g. after a progress reset) with the default. */
export function lockedEquipFix(p: Profile): Profile {
  let out = p;
  for (const cat of ['color', 'pattern', 'hat', 'eyes', 'honk', 'trail', 'scream', 'celebration'] as const) {
    if (!isUnlocked(out, cat, out.cosmetics[cat])) out = { ...out, cosmetics: { ...out.cosmetics, [cat]: cat === 'hat' ? 1 : 0 } };
  }
  return out;
}

/** The Bean Card's favorite map (§7.4): the arena with the most rounds played, or null before any. */
export function favoriteMap(p: Pick<Profile, 'stats'>): MapId | null {
  let best: MapId | null = null;
  let most = 0;
  for (const id of ARENAS) {
    const n = p.stats.mapRounds[id] ?? 0;
    if (n > most) {
      most = n;
      best = id;
    }
  }
  return best;
}
